import React, { useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useLanguage, useProvider } from '~/Hooks';
import { requestProviderLocationsList, selectLocationList } from '~/store/provider';
import { FilterCheckboxList } from '~/Components/Filter';
import { useSelectedMap } from '../useSelectedMap';
import { FilterType } from '~/Hooks/useFilter';
import { useProviderFilters } from '~/Hooks/useFilter/useProviderFilters';

export interface IUseLocationsGroupFilterProps {
  filterType: FilterType;
  initialFilter?: string[];
}

export const useLocationsGroupFilter = ({
  filterType,
  initialFilter = [],
}: IUseLocationsGroupFilterProps) => {
  const { appliedFilters } = useProviderFilters({ filterType });
  const { locationIds: initialState = initialFilter } = appliedFilters;
  const { t } = useLanguage();
  const dispatch = useDispatch();

  const { currentProvider } = useProvider();
  const getLocations = bindActionCreators(requestProviderLocationsList, dispatch);
  const locations = useSelector(selectLocationList);
  const list = useMemo(
    () =>
      locations
        .filter((location) => !location.archived)
        .map(({ uuid: id, name: label, suspended }) => {
          const deactivatedTag = suspended ? ` (${t('app.filter.deactivated')})` : '';

          return { id, label: `${label}${deactivatedTag}` };
        }),
    [t, locations],
  );
  const {
    selectItem,
    onClearMap,
    onApplyMap,
    selectedMap,
    selectedCount,
    updateSelectedMap,
    appliedSelectionIds,
  } = useSelectedMap({ initialState });

  useEffect(() => {
    if (currentProvider?.uuid && !locations.length) {
      getLocations(currentProvider.uuid);
    }
  }, [currentProvider]);

  return {
    name: 'locationIds',
    title: t('form.field.location'),
    elements: <FilterCheckboxList list={list} selectItem={selectItem} selectedMap={selectedMap} />,
    onClearMap,
    onApplyMap,
    selectedCount,
    updateSelectedMap,
    appliedSelectionIds,
  };
};
